import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminAPI } from '../services/api';
import { SmsSettings } from '../types';

interface SmsSettingsForm {
  provider_url: string;
  username: string;
  password: string;
  source_number: string;
  enabled: boolean;
  default_timeout: number;
  template_appointment_created: string;
  template_status_pending: string;
  template_status_in_progress: string;
  template_status_completed: string;
  template_status_cancelled: string;
  template_time_scheduled: string;
}

const PLACEHOLDER_HELP = (
  <ul className="list-disc pr-5 text-sm text-gray-600 space-y-1">
    <li>{'{full_name}'}: نام کامل بیمار</li>
    <li>{'{service_name}'}: نام خدمت</li>
    <li>{'{service_part}'}: عبارت « برای {'{service_name}'}» در صورت وجود خدمت</li>
    <li>{'{appointment_id}'}: شناسه نوبت</li>
    <li>{'{appointment_date}'} و {'{appointment_time}'}: تاریخ و ساعت مراجعه</li>
    <li>{'{cancel_reason}'}: دلیل لغو (در صورت وجود)</li>
    <li>{'{status}'}: وضعیت فعلی نوبت (pending, in_progress, ...)</li>
  </ul>
);

const defaultFormState: SmsSettingsForm = {
  provider_url: '',
  username: '',
  password: '',
  source_number: '',
  enabled: true,
  default_timeout: 5,
  template_appointment_created: '',
  template_status_pending: '',
  template_status_in_progress: '',
  template_status_completed: '',
  template_status_cancelled: '',
  template_time_scheduled: '',
};

const AdminSmsSettings: React.FC = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<SmsSettingsForm>(defaultFormState);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState<string>('');
  const [testMessage, setTestMessage] = useState<string>('');
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSuccessMessage, setTestSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role') || 'user';

    if (!token) {
      navigate('/admin/login');
      return;
    }

    // فقط admin می‌تواند به این صفحه دسترسی داشته باشد
    if (role !== 'admin') {
      navigate('/admin/dashboard');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await adminAPI.getSmsSettings();
        setFormState({
          provider_url: data.provider_url || '',
          username: data.username || '',
          password: data.password || '',
          source_number: data.source_number || '',
          enabled: data.enabled,
          default_timeout: data.default_timeout ?? 5,
          template_appointment_created: data.template_appointment_created || '',
          template_status_pending: data.template_status_pending || '',
          template_status_in_progress: data.template_status_in_progress || '',
          template_status_completed: data.template_status_completed || '',
          template_status_cancelled: data.template_status_cancelled || '',
          template_time_scheduled: data.template_time_scheduled || '',
        });
        setTestMessage(data.template_appointment_created || 'پیام آزمایشی سامانه رزرو');
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'خطا در دریافت تنظیمات پیامک');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (
    field: keyof SmsSettingsForm,
    value: string | number | boolean,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    const payload: Partial<SmsSettings> = {
      provider_url: formState.provider_url,
      username: formState.username,
      password: formState.password,
      source_number: formState.source_number,
      enabled: formState.enabled,
      default_timeout: formState.default_timeout,
      template_appointment_created: formState.template_appointment_created,
      template_status_pending: formState.template_status_pending,
      template_status_in_progress: formState.template_status_in_progress,
      template_status_completed: formState.template_status_completed,
      template_status_cancelled: formState.template_status_cancelled,
      template_time_scheduled: formState.template_time_scheduled,
    };

    try {
      await adminAPI.updateSmsSettings(payload);
      setSuccessMessage('تنظیمات پیامک با موفقیت ذخیره شد');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    setTestError(null);
    setTestSuccessMessage(null);

    if (!testPhone.trim()) {
      setTestError('شماره مقصد را وارد کنید');
      return;
    }

    if (!testMessage.trim()) {
      setTestError('متن پیام را وارد کنید');
      return;
    }

    setTestLoading(true);
    try {
      await adminAPI.testSms({ phone: testPhone.trim(), message: testMessage });
      setTestSuccessMessage('پیامک آزمایشی با موفقیت ارسال شد');
    } catch (err: any) {
      setTestError(err?.response?.data?.detail || 'ارسال پیامک آزمایشی با خطا مواجه شد');
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری تنظیمات پیامک..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">تنظیمات پیامک</h2>
            <p className="text-gray-600 text-sm">
              اطلاعات اتصال به سرویس پیامکی و متن پیام‌ها در وضعیت‌های مختلف را از این بخش مدیریت کنید.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" style={{ direction: 'rtl' }}>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">تنظیمات اتصال</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">آدرس سرویس (URL)</label>
                  <input
                    type="text"
                    value={formState.provider_url}
                    onChange={(e) => handleChange('provider_url', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://sms.asanak.ir/webservice/v2rest/sendsms"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری</label>
                  <input
                    type="text"
                    value={formState.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رمز عبور</label>
                  <input
                    type="text"
                    value={formState.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">شماره فرستنده</label>
                  <input
                    type="text"
                    value={formState.source_number}
                    onChange={(e) => handleChange('source_number', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: 9821xxxxxxx"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">مهلت انتظار (ثانیه)</label>
                  <input
                    type="number"
                    min={1}
                    value={formState.default_timeout}
                    onChange={(e) => handleChange('default_timeout', Number(e.target.value) || 5)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-3 space-x-reverse">
                  <input
                    id="sms-enabled"
                    type="checkbox"
                    checked={formState.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sms-enabled" className="text-sm font-medium text-gray-700">
                    ارسال پیامک فعال باشد
                  </label>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">متن پیام‌ها</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">راهنمای متغیرها</h4>
                {PLACEHOLDER_HELP}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">پیام ثبت نوبت</label>
                  <textarea
                    value={formState.template_appointment_created}
                    onChange={(e) => handleChange('template_appointment_created', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="{full_name} عزیز، درخواست نوبت شما..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">پیام وضعیت در انتظار</label>
                    <textarea
                      value={formState.template_status_pending}
                      onChange={(e) => handleChange('template_status_pending', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">پیام وضعیت در حال بررسی</label>
                    <textarea
                      value={formState.template_status_in_progress}
                      onChange={(e) => handleChange('template_status_in_progress', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">پیام تکمیل نوبت</label>
                    <textarea
                      value={formState.template_status_completed}
                      onChange={(e) => handleChange('template_status_completed', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">پیام لغو نوبت</label>
                    <textarea
                      value={formState.template_status_cancelled}
                      onChange={(e) => handleChange('template_status_cancelled', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">پیام تعیین زمان مراجعه</label>
                  <textarea
                    value={formState.template_time_scheduled}
                    onChange={(e) => handleChange('template_time_scheduled', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>
            </section>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
              >
                {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
              </button>
            </div>
          </form>

          <hr className="my-8" />

          <section className="space-y-4" style={{ direction: 'rtl' }}>
            <h3 className="text-lg font-semibold text-gray-800">ارسال پیامک آزمایشی</h3>
            <p className="text-sm text-gray-600">
              برای اطمینان از صحت تنظیمات، یک پیام آزمایشی به شماره دلخواه ارسال کنید.
            </p>

            {testError && (
              <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                {testError}
              </div>
            )}

            {testSuccessMessage && (
              <div className="p-4 rounded-md bg-green-50 border border-green-200 text-sm text-green-700">
                {testSuccessMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">شماره مقصد</label>
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="مثال: 0912xxxxxxx"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">متن پیام آزمایشی</label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleTestSend}
                disabled={testLoading}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-60"
              >
                {testLoading ? 'در حال ارسال...' : 'ارسال پیامک آزمایشی'}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>

  );
};

export default AdminSmsSettings;

